import InfoIcon from '@mui/icons-material/Info';
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import CustomButton from 'lib/designSystem/Button';
import EnhancedTable from 'lib/designSystem/Table';
import React, { useCallback, useEffect, useState } from 'react';

import { ChartDataResponse } from '..';

type AnalysisType = 'client' | 'stakeholder' | 'executive';

interface DailyAnalysisProps {
  data: ChartDataResponse[];
  businessesNames: string[];
  stakeholdersNames: string[];
  executivesNames: string[];
  creditLines: Record<string, number>;
}

type ItemTotals = Record<
  keyof Omit<
    ChartDataResponse,
    'name' | 'businessName' | 'stakeholderName' | 'executiveName'
  >,
  number
> & {
  executiveName?: string;
  creditLineAmount?: number;
  creditLineBalance?: number;
  itemData?: ChartDataResponse[];
};

interface ItemDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  item: ChartDataResponse[];
}

const ItemDetailsDialog: React.FC<ItemDetailsDialogProps> = ({
  open,
  onClose,
  item,
}) => {
  const tableData = item || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>Detalles de Colocación</DialogTitle>
      <DialogContent>
        <EnhancedTable
          data={tableData}
          dataHeaders={[
            { key: 'businessName', label: 'Cliente' },
            { key: 'businessIdentifier', label: 'Rut cliente' },
            { key: 'stakeholderName', label: 'Deudor' },
            { key: 'stakeholderIdentifier', label: 'Rut deudor' },
            { key: 'folio', label: 'Folio' },
            { key: 'paymentDate', label: 'Vencimiento', type: 'date' },
            { key: 'executiveName', label: 'Ejecutivo' },
            {
              key: 'Colocación total',
              label: 'Colocación total',
              type: 'money',
            },
            {
              key: 'Colocación propia',
              label: 'Colocación propia',
              type: 'money',
            },
            {
              key: 'Colocación fondos',
              label: 'Colocación fondos',
              type: 'money',
            },
            { key: 'Colocación sin mora', label: 'Sin mora', type: 'money' },
            {
              key: 'Colocación con mora 1-30 días',
              label: '1-30 días',
              type: 'money',
            },
            {
              key: 'Colocación con mora 31-60 días',
              label: '31-60 días',
              type: 'money',
            },
            {
              key: 'Colocación con mora 61-90 días',
              label: '61-90 días',
              type: 'money',
            },
            {
              key: 'Colocación con mora 91+ días',
              label: '91+ días',
              type: 'money',
            },
          ]}
          finalSumRow
          exportToFile="xlsx"
          rowsPerPageDefault={10}
        />
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={onClose} color="secondary">
          Cerrar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

const DailyAnalysis: React.FC<DailyAnalysisProps> = ({
  data,
  businessesNames,
  stakeholdersNames,
  executivesNames,
  creditLines,
}) => {
  const [analysisType, setAnalysisType] = useState<AnalysisType>('client');
  const [selectedStakeholders, setSelectedStakeholders] = useState<string[]>(
    [],
  );
  const [selectedExecutives, setSelectedExecutives] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  // Encontrar la fecha más reciente en los datos
  const parseCustomDate = useCallback((dateStr: string) => {
    // Convertir de 'DD-MM-YY' a un objeto dayjs
    const [day, month, year] = dateStr.split('-');
    return dayjs(`20${year}-${month}-${day}`);
  }, []);

  const getLatestDate = useCallback(() => {
    if (data.length === 0) return dayjs();

    const dates = data.map((item) => parseCustomDate(item.name));
    const latestDate = dates.reduce((latest, current) => {
      return current.isAfter(latest) ? current : latest;
    });
    return latestDate;
  }, [data, parseCustomDate]);

  const [selectedDate, setSelectedDate] = useState<Dayjs>(getLatestDate());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ChartDataResponse[] | null>(
    null,
  );

  useEffect(() => {
    if (data.length > 0) {
      setSelectedDate(getLatestDate());
    }
  }, [data, getLatestDate]);

  const getDailyData = () => {
    const formattedDate = selectedDate.format('DD-MM-YY');
    let filteredData = data.filter((item) => item.name === formattedDate);
    if (filteredData.length === 0) {
      return [];
    }

    // Aplicar filtros contextuales
    if (selectedStakeholders.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedStakeholders.includes(item.stakeholderName),
      );
    }
    if (selectedExecutives.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedExecutives.includes(item.executiveName),
      );
    }
    if (selectedClients.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedClients.includes(item.businessName),
      );
    }

    const getItemsList = () => {
      switch (analysisType) {
        case 'client':
          return businessesNames;
        case 'stakeholder':
          return stakeholdersNames;
        case 'executive':
          return [...executivesNames, ''];
        default:
          return [];
      }
    };

    const getFilterField = () => {
      switch (analysisType) {
        case 'client':
          return 'businessName' as keyof ChartDataResponse;
        case 'stakeholder':
          return 'stakeholderName' as keyof ChartDataResponse;
        case 'executive':
          return 'executiveName' as keyof ChartDataResponse;
        default:
          return 'businessName' as keyof ChartDataResponse;
      }
    };

    type ItemTotals = Record<
      keyof Omit<
        ChartDataResponse,
        'name' | 'businessName' | 'stakeholderName' | 'executiveName'
      >,
      number
    > & {
      executiveName?: string;
      creditLineAmount?: number;
      creditLineBalance?: number;
      itemData?: ChartDataResponse[];
    };
    const itemMap: Record<string, ItemTotals> = {};
    const items = getItemsList();
    const filterField = getFilterField();

    items.forEach((item) => {
      const itemData =
        item === ''
          ? filteredData.filter((dataItem) => !dataItem[filterField])
          : filteredData.filter((dataItem) => dataItem[filterField] === item);

      console.log('Filtered data for item:', item, itemData);

      const executiveName =
        analysisType === 'client' && itemData.length > 0
          ? itemData[0].executiveName
          : undefined;
      const creditLineAmount =
        analysisType === 'client' && itemData.length > 0
          ? creditLines[itemData[0].businessName]
          : undefined;

      const totals = itemData.reduce(
        (acc, curr) => {
          acc['Colocación total'] += curr['Colocación total'] || 0;
          acc['Colocación propia'] += curr['Colocación propia'] || 0;
          acc['Colocación fondos'] += curr['Colocación fondos'] || 0;
          acc['Colocación sin mora'] += curr['Colocación sin mora'] || 0;
          acc['Colocación con mora 1-30 días'] +=
            curr['Colocación con mora 1-30 días'] || 0;
          acc['Colocación con mora 31-60 días'] +=
            curr['Colocación con mora 31-60 días'] || 0;
          acc['Colocación con mora 61-90 días'] +=
            curr['Colocación con mora 61-90 días'] || 0;
          acc['Colocación con mora 91+ días'] +=
            curr['Colocación con mora 91+ días'] || 0;
          return acc;
        },
        {
          'Colocación total': 0,
          'Colocación propia': 0,
          'Colocación fondos': 0,
          'Colocación sin mora': 0,
          'Colocación con mora 1-30 días': 0,
          'Colocación con mora 31-60 días': 0,
          'Colocación con mora 61-90 días': 0,
          'Colocación con mora 91+ días': 0,
        },
      );

      const itemTotals: ItemTotals = {
        ...totals,
        executiveName,
        creditLineAmount,
        itemData: [...itemData], // Crear una nueva copia del array
        creditLineBalance: creditLineAmount
          ? creditLineAmount - totals['Colocación total']
          : undefined,
      };

      itemMap[item] = itemTotals;
    });

    const mappedData = Object.entries(itemMap).map(([item, values], index) => {
      const result = {
        id: index,
        [analysisType === 'client'
          ? 'Cliente'
          : analysisType === 'stakeholder'
            ? 'Deudor'
            : 'Ejecutivo']: item,
        ...values,
      };
      return result;
    });

    return mappedData;
  };

  const handleClickItem = (item: ChartDataResponse[]) => {
    console.log('Item clicked:', item);
    setSelectedItem(item);
    setDialogOpen(true);
  };

  const getTableHeaders = () => {
    const itemLabel =
      analysisType === 'client'
        ? 'Cliente'
        : analysisType === 'stakeholder'
          ? 'Deudor'
          : 'Ejecutivo';
    return [
      {
        key: 'itemData',
        label: 'Detalles',
        align: 'left',
        type: 'custom',
        customComponent: (item: ChartDataResponse[]) => {
          return (
            <IconButton
              size="small"
              onClick={() => {
                handleClickItem(item);
              }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          );
        },
      },
      {
        key: itemLabel,
        label: itemLabel,
        align: 'left',
      },
      ...(analysisType === 'client'
        ? [
            {
              key: 'executiveName',
              label: 'Ejecutivo',
              align: 'left',
            },
            {
              key: 'creditLineAmount',
              label: 'Línea de crédito',
              align: 'right',
              type: 'money',
            },
            {
              key: 'creditLineBalance',
              label: 'Disponible',
              align: 'right',
              type: 'money',
            },
          ]
        : []),
      {
        key: 'Colocación total',
        label: 'Colocación total',
        align: 'right',
        type: 'money',
      },
      {
        key: 'Colocación propia',
        label: 'Colocación propia',
        align: 'right',
        type: 'money',
      },
      {
        key: 'Colocación fondos',
        label: 'Colocación fondos',
        align: 'right',
        type: 'money',
      },
      {
        key: 'Colocación sin mora',
        label: 'Colocación sin mora',
        align: 'right',
        type: 'money',
      },
      {
        key: 'Colocación con mora 1-30 días',
        label: 'Mora 1-30 días',
        align: 'right',
        type: 'money',
      },
      {
        key: 'Colocación con mora 31-60 días',
        label: 'Mora 31-60 días',
        align: 'right',
        type: 'money',
      },
      {
        key: 'Colocación con mora 61-90 días',
        label: 'Mora 61-90 días',
        align: 'right',
        type: 'money',
      },
      {
        key: 'Colocación con mora 91+ días',
        label: 'Mora 91+ días',
        align: 'right',
        type: 'money',
      },
    ];
  };

  const getAvailableFilters = () => {
    switch (analysisType) {
      case 'client':
        return [
          {
            label: 'Deudores',
            value: selectedStakeholders,
            setValue: setSelectedStakeholders,
            options: stakeholdersNames,
          },
          {
            label: 'Ejecutivos',
            value: selectedExecutives,
            setValue: setSelectedExecutives,
            options: executivesNames,
          },
        ];
      case 'stakeholder':
        return [
          {
            label: 'Clientes',
            value: selectedClients,
            setValue: setSelectedClients,
            options: businessesNames,
          },
          {
            label: 'Ejecutivos',
            value: selectedExecutives,
            setValue: setSelectedExecutives,
            options: executivesNames,
          },
        ];
      case 'executive':
        return [
          {
            label: 'Clientes',
            value: selectedClients,
            setValue: setSelectedClients,
            options: businessesNames,
          },
          {
            label: 'Deudores',
            value: selectedStakeholders,
            setValue: setSelectedStakeholders,
            options: stakeholdersNames,
          },
        ];
      default:
        return [];
    }
  };

  return (
    <div style={{ marginTop: '32px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <FormControl>
            <Typography variant="caption" style={{ marginBottom: '4px' }}>
              Ver por
            </Typography>
            <Select
              value={analysisType}
              onChange={(event) =>
                setAnalysisType(event.target.value as AnalysisType)
              }
              size="small"
              style={{ width: '150px' }}
            >
              <MenuItem value="client">Por Cliente</MenuItem>
              <MenuItem value="stakeholder">Por Deudor</MenuItem>
              <MenuItem value="executive">Por Ejecutivo</MenuItem>
            </Select>
          </FormControl>

          <FormControl>
            <Typography variant="caption" style={{ marginBottom: '4px' }}>
              Fecha a analizar
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedDate}
                onChange={(newValue: Dayjs | null) => {
                  if (newValue) {
                    setSelectedDate(newValue);
                  }
                }}
                format="DD-MM-YY"
                slotProps={{
                  textField: {
                    size: 'small',
                    style: { width: '150px' },
                  },
                }}
              />
            </LocalizationProvider>
          </FormControl>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {getAvailableFilters().map((filter) => (
            <FormControl key={filter.label}>
              <Typography variant="caption" style={{ marginBottom: '4px' }}>
                Filtrar por {filter.label.toLowerCase()}
              </Typography>
              <Select
                multiple
                value={filter.value}
                onChange={(event) => {
                  const value = event.target.value as string[];
                  filter.setValue(value);
                }}
                size="small"
                style={{ width: '200px' }}
                renderValue={(selected) =>
                  `${filter.label}: ${selected.join(', ')}`
                }
              >
                {filter.options.map((option) => (
                  <MenuItem key={option} value={option}>
                    <Checkbox checked={filter.value.includes(option)} />
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </div>
      </div>

      <div style={{ flex: 1 }}>
        <EnhancedTable
          data={getDailyData()}
          dataHeaders={getTableHeaders()}
          rowsPerPageDefault={10}
          exportToFile="xlsx"
          fileName={`analisis_${analysisType}_${selectedDate.format('DD-MM-YY')}.xlsx`}
          finalSumRow
        />
      </div>
      {selectedItem && (
        <ItemDetailsDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setSelectedItem(null);
          }}
          item={selectedItem}
        />
      )}
    </div>
  );
};

export default DailyAnalysis;
